/** 分析サマリー / セグメント条件で扱う経路の区分（閉集合 = zod enum と同期）。 */
export const FIRST_SOURCE_BUCKETS = [
    'lineads',
    'poster',
    'event202611',
    'richmenu',
    'other',
    'none',
];
/** `?s=` の値のうち、区分として名指しで扱うもの。それ以外は `other`。 */
const NAMED_SOURCES = new Set([
    'lineads',
    'poster',
    'event202611',
    'richmenu',
]);
/** 入口（`?s=`）を運ぶ顧客起点の種別。これ以外の `payload.source` は入口と見なさない。 */
export const FIRST_SOURCE_EVENT_TYPES = new Set([
    'liff_link_tap',
    'richmenu_tap',
    // 抽選券 QR → 抽選まで到達した記録（source は券の設定値 = event202611）。
    'lottery_draw',
]);
/** 新規判定で「それ以前の行動」と見なさない種別（顧客が何もしていなくても記録されるもの）。 */
const NOT_PRIOR_ACTIVITY = new Set([
    'follow',
    'liff_visit',
    'message_sent',
    'stage_changed',
    'cycle_reset',
    'hot_won',
]);
/**
 * 入口として採る source を返す（空・非文字列・入口を運ばない種別は undefined）。
 * 種別を見るのは、サーバ発の `message_sent` 等にも `payload.source` が載るため。
 */
export function normalizeEventSource(eventType, payload) {
    if (!FIRST_SOURCE_EVENT_TYPES.has(eventType))
        return undefined;
    const raw = payload?.source;
    if (typeof raw !== 'string')
        return undefined;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}
/** 経路の生の値 → 区分。未設定は `none`、名指し以外は `other`。 */
export function firstSourceBucketOf(source) {
    if (!source)
        return 'none';
    return NAMED_SOURCES.has(source) ? source : 'other';
}
/** 顧客の区分（`AppUser.firstSource` から）。 */
export function userFirstSourceBucket(user) {
    return firstSourceBucketOf(user.firstSource);
}
/**
 * 行動ログ全件（順序不問）から最初の入口を導出する。`?s=` 付きの行動ログが無ければ undefined。
 * 同時刻は入口の方を先と見なす（同秒の `liff_visit` 等は NOT_PRIOR_ACTIVITY で除かれる）。
 */
export function deriveFirstSource(events) {
    const sorted = [...events].sort((a, b) => a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0);
    const entry = sorted.find((e) => normalizeEventSource(e.eventType, e.payload) !== undefined);
    if (!entry)
        return undefined;
    const firstSourceIsNew = !sorted.some((e) => e.createdAt < entry.createdAt && !NOT_PRIOR_ACTIVITY.has(e.eventType));
    return {
        firstSource: normalizeEventSource(entry.eventType, entry.payload),
        firstSourceAt: entry.createdAt,
        firstSourceIsNew,
    };
}
/**
 * 増分経路用: 既存 user doc の集計値から「入口を通る前に行動があったか」を判定する。
 * `eventTypeCounts` に follow / liff_visit 以外があるか、予約回数が 1 以上なら既存。
 * （全件再集計は予約を reservation_completed に合成して同じ扱いにする）
 */
export function hasPriorActivity(user) {
    if ((user.bookingCount ?? 0) > 0)
        return true;
    for (const [type, count] of Object.entries(user.eventTypeCounts ?? {})) {
        if (NOT_PRIOR_ACTIVITY.has(type))
            continue;
        if ((count ?? 0) > 0)
            return true;
    }
    return false;
}
//# sourceMappingURL=promoFirstSource.js.map